import { useState } from "react";
import { Sparkles } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Spinner } from "@/components/ui/spinner";
import { formatCalendarDate } from "@/lib/format";

/** The slim strip over every page of a demo organisation: what it is, when it ends, and a way to start again. */
export function DemoBanner({ expiresAt, onReset }: { expiresAt: string | null; onReset: () => Promise<boolean> }) {
  const [confirming, setConfirming] = useState(false);
  const [resetting, setResetting] = useState(false);

  async function reset() {
    setResetting(true);
    const ok = await onReset();
    setResetting(false);
    if (ok) setConfirming(false);
  }

  return (
    <div className="flex min-h-8 flex-wrap items-center justify-center gap-x-2 gap-y-0.5 bg-primary px-4 py-1.5 text-center text-xs text-primary-foreground">
      <Sparkles className="size-3.5 shrink-0 opacity-80" aria-hidden />
      <span className="font-medium">You're exploring a Dolfin demo</span>
      {expiresAt ? (
        <>
          <span aria-hidden className="opacity-50">
            ·
          </span>
          <span className="opacity-85">Expires {formatCalendarDate(expiresAt)}</span>
        </>
      ) : null}
      <span aria-hidden className="opacity-50">
        ·
      </span>
      <button
        type="button"
        onClick={() => setConfirming(true)}
        className="font-medium underline underline-offset-2 hover:opacity-90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-foreground"
      >
        Reset demo
      </button>

      <AlertDialog open={confirming} onOpenChange={(o) => !resetting && setConfirming(o)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset the demo?</AlertDialogTitle>
            <AlertDialogDescription>
              Everything you changed is undone and the business is rebuilt from scratch, dated to today. It only takes a
              moment.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={resetting}>Keep my changes</AlertDialogCancel>
            <AlertDialogAction
              disabled={resetting}
              onClick={(e) => {
                e.preventDefault();
                void reset();
              }}
            >
              {resetting ? <Spinner className="size-4" /> : null} Reset demo
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
